import { ChangeDetectionStrategy, ChangeDetectorRef, Component, EventEmitter, Inject, Input, OnChanges, OnInit, Optional, Output, SimpleChanges } from '@angular/core';
import { Base64UrlService } from '../../../services/base64url.service';
import { ModalService } from '../../../core/services/modal.service';
import { DocPackTypeGet, DocPackTypeService, GetDocPackTypeParams } from '@app/services/doc-pack-type.service';
import { TT_VIEW_NAVIGATOR, TTViewNavigator, useNavigatorFor } from '@app/modal/state-modal/state-modal.component';

@Component({
    selector: 'tt-doc-pack-type',
    templateUrl: './doc-pack-type.component.html',
    styleUrls: ['./doc-pack-type.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DocPackTypeComponent implements OnChanges, OnInit {
    @Input()
    public params?: GetDocPackTypeParams;

    @Output()
    public submit = new EventEmitter<DocPackTypeSubmitEvent>();

    public edit?: DocPackTypeGet;

    public saving = false;

    public deleting = false;

    constructor(
        private docPackType: DocPackTypeService,
        private base64: Base64UrlService,
        private modal: ModalService,
        private cdr: ChangeDetectorRef,
        @Optional() @Inject(TT_VIEW_NAVIGATOR) private navigator?: TTViewNavigator,
    ) {
        this.navigator = useNavigatorFor(navigator, this);
    }

    private async loadEdit() {
        if (!!this.params) {
            this.edit = await this.docPackType.getDocPackType(this.params);

            if (!!this.edit) {
                this.edit.html_text_frontpage = this.base64.urlDecode(this.edit.html_text_frontpage);
                this.edit.html_text_indexpage = this.base64.urlDecode(this.edit.html_text_indexpage);
            }

            this.cdr.markForCheck();
        }
    }

    public async saveChanges() {
        if (!!this.edit) {
            try {
                this.saving = true;
                this.cdr.markForCheck();

                const response = await this.docPackType.saveDocPackType({ ...this.edit, html_text_frontpage: this.base64.urlEncode(this.edit.html_text_frontpage), html_text_indexpage: this.base64.urlEncode(this.edit.html_text_indexpage) });

                if (response?.errorcode !== '0') {
                    this.modal.show({ type: 'warning', title: 'error', translateTitle: true, message: response.errormessage });
                } else {
                    this.navigator?.back();
                    this.submit.next({ type: 'save', ...response });
                }
            } finally {
                this.saving = false;
                this.cdr.markForCheck();
            }
        }
    }

    public async deleteValues() {
        if (!!this.edit) {
            try {
                this.deleting = true;
                this.cdr.markForCheck();

                const deleteConfirmed = await this.modal.openConfirmDialog({ title: 'confirm_title', translateMessage: true, message: 'confirm_message', type: 'danger' });

                if (deleteConfirmed) {
                    const response = await this.docPackType.deleteDocPackType(this.edit);

                    if ('' + response.errorcode !== '0') {
                        this.modal.show({ type: 'warning', title: 'error', message: response.errormessage });
                    } else {
                        this.navigator?.back();
                        this.submit.next({ type: 'delete', docpacktype_keyno: this.edit.docpacktype_keyno });
                    }
                }
            } finally {
                this.deleting = false;
                this.cdr.markForCheck();
            }
        }
    }

    private async init() {
        await this.loadEdit();
    }

    ngOnInit(): void {
        if (!!this.navigator) {
            this.navigator.setSize?.({ height: '95vh' });
            this.params = this.navigator.params as any;
            this.init();
        }
    }

    ngOnChanges(changes: SimpleChanges): void {
        if (changes['params']) {
            this.init();
        }
    }
}

export interface DocPackTypeSubmitEvent {
    type: 'save' | 'delete';
    docpacktype_keyno: string;
}
